import { useEffect, useState } from 'react';
import { signingApi, documentApi, matterApi, type SigningEnvelope, type DocumentMeta, type MatterSummary } from '../api';
import Select from '../components/common/Select';
import { humaniseStatus } from '../lib/labels';

// Signing envelopes — list, create draft, add signer, send. The signer flow
// (typed signature + optional OTP) runs at /sign?e=&t= as a separate public
// page; advocates manage envelopes from here.

export default function SigningPage() {
  const [envelopes, setEnvelopes] = useState<SigningEnvelope[]>([]);
  const [status, setStatus] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [sentDialog, setSentDialog] = useState<{ env: string; sent: { email: string; url: string }[] } | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await signingApi.list(status ? { status } : {});
      setEnvelopes(r.envelopes);
    } catch (e) { setError((e as Error).message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const voidEnv = async (env: SigningEnvelope) => {
    if (!confirm(`Void "${env.title}"?`)) return;
    try { await signingApi.voidIt(env.ROWID); load(); }
    catch (e) { alert((e as Error).message); }
  };
  const send = async (env: SigningEnvelope) => {
    try {
      const r = await signingApi.send(env.ROWID);
      setSentDialog({ env: env.title, sent: r.sent });
      load();
    } catch (e) { alert((e as Error).message); }
  };

  const statusStyle = (s: string): string => {
    if (s === 'completed')   return 'success';
    if (s === 'in_progress') return 'info';
    if (s === 'sent')        return 'accent';
    if (s === 'voided' || s === 'declined' || s === 'expired') return 'danger';
    return '';
  };

  return (
    <div className="stack">
      <div className="row">
        <h1 className="grow">Signing</h1>
        <div style={{ width: 200 }}>
          <Select
            value={status}
            onChange={setStatus}
            options={[
              { value: '',            label: 'All statuses' },
              { value: 'draft',       label: 'Draft' },
              { value: 'sent',        label: 'Sent' },
              { value: 'in_progress', label: 'In progress' },
              { value: 'completed',   label: 'Completed' },
              { value: 'voided',      label: 'Voided' },
            ]}
          />
        </div>
        <button onClick={load}>Refresh</button>
        <button className="primary" onClick={() => setCreating(true)}>+ New envelope</button>
      </div>

      <div className="card">
        <b>How it works</b>
        <p className="muted" style={{ fontSize: 13, marginTop: 4 }}>
          Pick a document from the library, add one or more signers, hit Send. Each signer gets a tokenised link (and an email if SMTP is configured). OTP 2FA optional per signer. Envelope auto-completes when the last signer signs; the cert-of-completion PDF (with signatures + SHA256 hash + audit trail) lands as a new document on the matter.
        </p>
      </div>

      {error && <div className="card" style={{ borderColor: 'var(--color-danger)' }}>{error}</div>}

      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div className="muted" style={{ padding: 14 }}>Loading…</div>
        ) : envelopes.length === 0 ? (
          <p className="muted" style={{ padding: 14 }}>No envelopes yet. Click <b>+ New envelope</b> above to create your first one.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Status</th>
                <th>Matter</th>
                <th>Order</th>
                <th>Sent</th>
                <th>Completed</th>
                <th style={{ width: 200 }}></th>
              </tr>
            </thead>
            <tbody>
              {envelopes.map((e) => (
                <tr key={e.ROWID}>
                  <td style={{ fontWeight: 500 }}>{e.title}</td>
                  <td><span className={`pill ${statusStyle(e.status)}`}>{humaniseStatus(e.status)}</span></td>
                  <td>{e.matter_id ? `#${e.matter_id}` : <span className="muted">—</span>}</td>
                  <td><span className="pill">{e.signing_order}</span></td>
                  <td>{e.sent_at?.slice(0, 10) || <span className="muted">—</span>}</td>
                  <td>{e.completed_at?.slice(0, 10) || <span className="muted">—</span>}</td>
                  <td style={{ textAlign: 'right' }}>
                    {e.status === 'draft'  && <button onClick={() => send(e)}>Send</button>}
                    {e.status !== 'voided' && e.status !== 'completed' && (
                      <button onClick={() => voidEnv(e)} style={{ marginLeft: 6 }}>Void</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {creating && (
        <NewEnvelopeModal
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); load(); }}
        />
      )}

      {sentDialog && (
        <div style={backdrop} onClick={() => setSentDialog(null)}>
          <div className="card stack" style={{ width: 640 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: 0 }}>Sent: {sentDialog.env}</h3>
            <p className="muted" style={{ fontSize: 13, margin: 0 }}>
              {sentDialog.sent.length} signer link(s) generated. Email is best-effort — copy + paste any link below if you'd rather hand-deliver it.
            </p>
            <div className="card" style={{ padding: 0, maxHeight: 360, overflowY: 'auto' }}>
              <table>
                <thead><tr><th>Email</th><th>Link</th></tr></thead>
                <tbody>
                  {sentDialog.sent.map((s, i) => (
                    <tr key={i}>
                      <td className="muted" style={{ fontSize: 12 }}>{s.email}</td>
                      <td style={{ fontSize: 11, fontFamily: 'ui-monospace, monospace', wordBreak: 'break-all' }}>{s.url}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <button onClick={() => setSentDialog(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- New envelope modal ----------

type DraftSigner = {
  name: string;
  email: string;
  role: 'signer' | 'approver' | 'cc';
  require_otp: boolean;
};

function NewEnvelopeModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [title, setTitle] = useState('');
  const [matterId, setMatterId] = useState<string>('');
  const [matters, setMatters] = useState<MatterSummary[]>([]);
  const [docId, setDocId] = useState<string>('');
  const [docs, setDocs] = useState<DocumentMeta[]>([]);
  const [signingOrder, setSigningOrder] = useState<'serial' | 'parallel'>('parallel');
  const [message, setMessage] = useState('');
  const [signers, setSigners] = useState<DraftSigner[]>([{ name: '', email: '', role: 'signer', require_otp: false }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    matterApi.list({}).then((r) => setMatters(r.matters)).catch(() => null);
  }, []);

  // When the user picks a matter, narrow the document list to that matter.
  // Otherwise show every document in the chambers (capped server-side).
  useEffect(() => {
    documentApi.list(matterId ? { matter_id: matterId } : {})
      .then((r) => setDocs(r.documents))
      .catch(() => setDocs([]));
  }, [matterId]);

  const addSigner = () => setSigners([...signers, { name: '', email: '', role: 'signer', require_otp: false }]);
  const updateSigner = (i: number, patch: Partial<DraftSigner>) =>
    setSigners(signers.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const removeSigner = (i: number) => setSigners(signers.filter((_, idx) => idx !== i));

  const create = async () => {
    if (!title.trim())                 { setError('Title is required.'); return; }
    if (!docId)                        { setError('Pick the source document.'); return; }
    if (!signers.length)               { setError('Add at least one signer.'); return; }
    for (const s of signers) {
      if (!s.name.trim() || !s.email.trim()) { setError('Each signer needs a name and email.'); return; }
    }
    setSaving(true); setError(null);
    try {
      const env = await signingApi.create({
        title: title.trim(),
        source_document_id: Number(docId),
        matter_id: matterId ? Number(matterId) : undefined,
        signing_order: signingOrder,
        message: message || undefined,
      });
      // Sequential adds — backend auto-attaches a default signature field per signer.
      for (const s of signers) {
        await signingApi.addSigner(env.id, s);
      }
      onCreated();
    } catch (e) {
      setError((e as Error).message);
    } finally { setSaving(false); }
  };

  return (
    <div style={backdrop} onClick={onClose}>
      <div className="card" style={{ width: 760, maxWidth: '94vw', maxHeight: '88vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginBottom: 14 }}>New signing envelope</h3>
        <div className="stack">
          <div className="row">
            <div className="grow">
              <label>Title</label>
              <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Power of Attorney — Smith v Jones" />
            </div>
            <div style={{ width: 200 }}>
              <label>Signing order</label>
              <Select
                value={signingOrder}
                onChange={(v) => setSigningOrder(v as 'serial' | 'parallel')}
                options={[
                  { value: 'parallel', label: 'Parallel (any order)' },
                  { value: 'serial',   label: 'Serial (in order)' },
                ]}
              />
            </div>
          </div>

          <div className="row">
            <div className="grow">
              <label>Matter (optional)</label>
              <Select
                value={matterId}
                onChange={(v) => { setMatterId(v); setDocId(''); }}
                searchPlaceholder="Filter docs by matter…"
                options={[
                  { value: '', label: 'No matter / all documents' },
                  ...matters.map((m) => ({ value: String(m.ROWID), label: `${m.reference || `#${m.ROWID}`} — ${m.matter_name}` })),
                ]}
              />
            </div>
            <div className="grow">
              <label>Source document</label>
              <Select
                value={docId}
                onChange={setDocId}
                searchPlaceholder="Pick a document…"
                options={docs.map((d) => ({ value: String(d.ROWID), label: d.title }))}
              />
            </div>
          </div>

          <div>
            <label>Message to signers (optional)</label>
            <textarea rows={2} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="A short note that appears in the signing email." />
          </div>

          <div>
            <div className="row">
              <label className="grow">Signers</label>
              <button onClick={addSigner}>+ Add signer</button>
            </div>
            <div className="stack" style={{ marginTop: 8 }}>
              {signers.map((s, i) => (
                <div key={i} className="card" style={{ padding: 12, background: 'var(--color-surface-2)' }}>
                  <div className="row">
                    <div className="grow">
                      <input value={s.name} onChange={(e) => updateSigner(i, { name: e.target.value })} placeholder="Full name" />
                    </div>
                    <div className="grow">
                      <input type="email" value={s.email} onChange={(e) => updateSigner(i, { email: e.target.value })} placeholder="email@example.co.za" />
                    </div>
                    <div style={{ width: 140 }}>
                      <Select
                        value={s.role}
                        onChange={(v) => updateSigner(i, { role: v as DraftSigner['role'] })}
                        options={[
                          { value: 'signer',   label: 'Signer' },
                          { value: 'approver', label: 'Approver' },
                          { value: 'cc',       label: 'CC (no sign)' },
                        ]}
                      />
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, textTransform: 'none', letterSpacing: 0, color: 'var(--color-text)' }}>
                      <input type="checkbox" checked={s.require_otp} onChange={(e) => updateSigner(i, { require_otp: e.target.checked })} style={{ width: 'auto' }} />
                      OTP
                    </label>
                    {signers.length > 1 && (
                      <button className="ghost" style={{ color: 'var(--color-danger)', padding: '4px 8px' }} onClick={() => removeSigner(i)}>×</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}

          <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
            <button onClick={onClose} disabled={saving}>Cancel</button>
            <button className="primary" onClick={create} disabled={saving}>
              {saving ? 'Creating…' : 'Create draft'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const backdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
};
